import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faNewspaper,
  faFolder,
  faCalendarDays,
  faUser,
  faArrowRight,
  faBars,
  faTimes,
  faFire,
  faBookmark,
  faEye,
  faSearch,
  faXmark,
  faEnvelope
} from '@fortawesome/free-solid-svg-icons';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import './HomePage.css';

function HomePage() {
  const { isAuthenticated } = useAuth();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [siteSettings, setSiteSettings] = useState({
    site_name: 'Bonlineco Blog',
    site_description: 'Quality content for curious minds',
    site_url: 'https://blog.bonlineco.com'
  });

  useEffect(() => {
    trackVisit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Debounce search
    const timer = setTimeout(() => {
      loadData();
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, searchQuery]);

  const trackVisit = async () => {
    try {
      await apiService.incrementSiteVisit();
    } catch (error) {
      console.error('Error tracking visit:', error);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load settings first
      try {
        const settingsResponse = await apiService.getPublicSettings();
        setSiteSettings(settingsResponse.data);
      } catch (error) {
        console.error('Error loading settings:', error);
      }
      
      // Load posts
      const postsParams = { status: 'published' };
      if (selectedCategory) {
        postsParams.category_id = selectedCategory;
      }
      if (searchQuery.trim()) {
        postsParams.search = searchQuery.trim();
      }
      const postsResponse = await apiService.getPosts(postsParams);
      setPosts(postsResponse.data.data || postsResponse.data);

      // Load categories
      const categoriesResponse = await apiService.getCategories({ paginate: false });
      const categoriesData = Array.isArray(categoriesResponse.data) 
        ? categoriesResponse.data 
        : (categoriesResponse.data.data || []);
      setCategories(categoriesData);
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatViews = (views) => {
    if (views >= 1000000) {
      return (views / 1000000).toFixed(1) + 'M';
    } else if (views >= 1000) {
      return (views / 1000).toFixed(1) + 'K';
    }
    return views || 0;
  };

  const stripHtml = (html) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  return (
    <>
      <Helmet>
        <title>{siteSettings.site_name} - Latest Articles, News & Insights</title>
        <meta name="description" content={siteSettings.site_description} />
        <meta name="keywords" content="blog, articles, technology, business, lifestyle, news, insights" />
        <meta property="og:title" content={`${siteSettings.site_name} - Latest Articles & Insights`} />
        <meta property="og:description" content={siteSettings.site_description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={siteSettings.site_url} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={siteSettings.site_name} />
        <meta name="twitter:description" content={siteSettings.site_description} />
        <link rel="canonical" href={siteSettings.site_url} />
      </Helmet>

      <div className="homepage">
        {/* Header */}
        <header className="homepage-header">
          <div className="homepage-container">
            <div className="homepage-header-content">
              <div className="homepage-logo">
                <FontAwesomeIcon icon={faNewspaper} className="homepage-logo-icon" />
                <h1>{siteSettings.site_name}</h1>
              </div>
              
              <button 
                className="homepage-mobile-menu"
                onClick={() => setMenuOpen(!menuOpen)}
                aria-label="Toggle menu"
              >
                <FontAwesomeIcon icon={menuOpen ? faTimes : faBars} />
              </button>

              <nav className={`homepage-nav ${menuOpen ? 'homepage-nav-open' : ''}`}>
                <a href="/" className="homepage-nav-link">
                  <FontAwesomeIcon icon={faFire} />
                  Home
                </a>
                <a href="#categories" className="homepage-nav-link">
                  <FontAwesomeIcon icon={faFolder} />
                  Categories
                </a>
                <a href="https://bonlineco.com" className="homepage-nav-link" target="_blank" rel="noopener noreferrer">
                  <FontAwesomeIcon icon={faNewspaper} />
                  Services
                </a>
                <a href="https://store.bonlineco.com" className="homepage-nav-link" target="_blank" rel="noopener noreferrer">
                  <FontAwesomeIcon icon={faBookmark} />
                  Store
                </a>
                <a href="/contact" className="homepage-nav-link">
                  <FontAwesomeIcon icon={faEnvelope} />
                  Contact
                </a>
                {isAuthenticated && (
                  <a href="/admin_v3jq5z/dashboard" className="homepage-nav-link homepage-nav-link-admin">
                    <FontAwesomeIcon icon={faBookmark} />
                    Admin
                  </a>
                )}
              </nav>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="homepage-hero">
          <div className="homepage-container">
            <div className="homepage-hero-content">
              <span className="homepage-hero-badge">
                <FontAwesomeIcon icon={faFire} />
                Welcome to Our Blog
              </span>
              <h2 className="homepage-hero-title">
                Discover Stories, Insights & Ideas
              </h2>
              <p className="homepage-hero-subtitle">
                Explore quality content on technology, business, lifestyle, and more. 
                Stay informed and inspired with our latest articles.
              </p>
            </div>
          </div>
        </section>

        {/* Categories Filter */}
        <section id="categories" className="homepage-categories">
          <div className="homepage-container">
            <div className="homepage-section-header">
              <h3>
                <FontAwesomeIcon icon={faFolder} />
                Browse by Category
              </h3>
            </div>
            <div className="homepage-categories-grid">
              <button
                className={`homepage-category-chip ${!selectedCategory ? 'active' : ''}`}
                onClick={() => setSelectedCategory(null)}
              >
                All Posts
              </button>
              {categories.map(category => (
                <button
                  key={category.id}
                  className={`homepage-category-chip ${selectedCategory === category.id ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(category.id)}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Search Bar */}
        <section className="homepage-search-section">
          <div className="homepage-container">
            <div className="homepage-search-container">
              <div className="homepage-search-wrapper">
                <FontAwesomeIcon icon={faSearch} className="homepage-search-icon" />
                <input
                  type="text"
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="homepage-search-input"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="homepage-search-clear"
                    aria-label="Clear search"
                  >
                    <FontAwesomeIcon icon={faXmark} />
                  </button>
                )}
              </div>
              {searchQuery && (
                <p className="homepage-search-info">
                  {loading ? 'Searching...' : `Found ${posts.length} result${posts.length !== 1 ? 's' : ''}`}
                </p>
              )}
            </div>
          </div>
        </section>

        {/* Posts Grid */}
        <section className="homepage-posts">
          <div className="homepage-container">
            {loading ? (
              <div className="homepage-loading">
                <div className="homepage-spinner"></div>
                <p>Loading posts...</p>
              </div>
            ) : posts.length === 0 ? (
              <div className="homepage-empty">
                <FontAwesomeIcon icon={faNewspaper} />
                <h3>No posts found</h3>
                <p>There are no published posts in this category yet.</p>
              </div>
            ) : (
              <div className="homepage-posts-grid">
                {posts.map(post => (
                  <article key={post.id} className="homepage-post-card">
                    {post.featured_image && (
                      <div className="homepage-post-image">
                        <img src={post.featured_image} alt={post.title} />
                        <div className="homepage-post-overlay"></div>
                      </div>
                    )}
                    <div className="homepage-post-content">
                      {post.category && (
                        <span className="homepage-post-category">
                          <FontAwesomeIcon icon={faFolder} />
                          {post.category.name}
                        </span>
                      )}
                      <h3 className="homepage-post-title">{post.title}</h3>
                      {post.excerpt && (
                        <p className="homepage-post-excerpt">
                          {stripHtml(post.excerpt)}
                        </p>
                      )}
                      <div className="homepage-post-meta">
                        <span className="homepage-post-meta-item">
                          <FontAwesomeIcon icon={faCalendarDays} />
                          {formatDate(post.created_at)}
                        </span>
                        {post.user && (
                          <span className="homepage-post-meta-item">
                            {(post.user.avatar && post.user.avatar.trim() !== '') ? (
                              <img 
                                src={`https://blog.bonlineco.com/storage/${post.user.avatar}`} 
                                alt={post.user.name}
                                className="homepage-author-avatar"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            ) : (
                              <FontAwesomeIcon icon={faUser} />
                            )}
                            {post.user.name}
                          </span>
                        )}
                        <span className="homepage-post-meta-item homepage-post-views">
                          <FontAwesomeIcon icon={faEye} />
                          <span className="homepage-view-count">{formatViews(post.views)}</span>
                          <span className="homepage-view-label">views</span>
                        </span>
                      </div>
                      <Link to={`/post/${post.slug}`} className="homepage-post-link">
                        Read More
                        <FontAwesomeIcon icon={faArrowRight} />
                      </Link>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Footer */}
        <footer className="homepage-footer">
          <div className="homepage-container">
            <div className="homepage-footer-content">
              <div className="homepage-footer-brand">
                <FontAwesomeIcon icon={faNewspaper} className="homepage-footer-icon" />
                <h4>{siteSettings.site_name}</h4>
                <p>{siteSettings.site_description}</p>
              </div>
              <div className="homepage-footer-links">
                <div className="homepage-footer-section">
                  <h5>Quick Links</h5>
                  <a href="/">Home</a>
                  <a href="#categories">Categories</a>
                  <a href="/contact">Contact</a>
                  <a href="https://bonlineco.com" target="_blank" rel="noopener noreferrer">Services</a>
                  <a href="https://store.bonlineco.com" target="_blank" rel="noopener noreferrer">Store</a>
                  {isAuthenticated && (
                    <a href="/admin_v3jq5z/dashboard">Admin Panel</a>
                  )}
                </div>
                <div className="homepage-footer-section">
                  <h5>Categories</h5>
                  {categories.slice(0, 5).map(cat => (
                    <a key={cat.id} href={`#cat-${cat.slug}`} onClick={() => setSelectedCategory(cat.id)}>
                      {cat.name}
                    </a>
                  ))}
                </div>
              </div>
            </div>
            <div className="homepage-footer-bottom">
              <p>&copy; {new Date().getFullYear()} {siteSettings.site_name}. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}

export default HomePage;
